import { GameConfig, Vector2, Rectangle } from '../types/game.types';

export class RenderManager {
  private ctx: CanvasRenderingContext2D;
  private config: GameConfig;
  private pixelRatio: number;

  constructor(ctx: CanvasRenderingContext2D, config: GameConfig) {
    this.ctx = ctx;
    this.config = config;
    this.pixelRatio = window.devicePixelRatio || 1;
    
    this.setupContext();
  }
  
  public getCanvas(): HTMLCanvasElement {
    return this.ctx.canvas;
  }

  private setupContext(): void {
    // Disable image smoothing for pixel-perfect rendering
    this.ctx.imageSmoothingEnabled = false;
    
    // Set default styles
    this.ctx.fillStyle = '#FFFFFF';
    this.ctx.strokeStyle = '#FFFFFF';
    this.ctx.lineWidth = 1;
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'top';
  }

  public clear(color: string = '#40318D'): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, this.config.width, this.config.height);
  }

  public fillRect(x: number, y: number, width: number, height: number, color: string = '#FFFFFF'): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(Math.floor(x), Math.floor(y), Math.floor(width), Math.floor(height));
  }

  public strokeRect(x: number, y: number, width: number, height: number, color: string = '#FFFFFF', lineWidth: number = 1): void {
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = lineWidth;
    this.ctx.strokeRect(Math.floor(x) + 0.5, Math.floor(y) + 0.5, Math.floor(width), Math.floor(height));
  }

  public drawText(text: string, x: number, y: number, color: string = '#FFFFFF', font: string = '14px "Courier New", monospace'): void {
    this.ctx.fillStyle = color;
    this.ctx.font = font;
    this.ctx.fillText(text, Math.floor(x), Math.floor(y));
  }

  public drawTextCentered(text: string, x: number, y: number, color: string = '#FFFFFF', font: string = '14px "Courier New", monospace'): void {
    this.ctx.fillStyle = color;
    this.ctx.font = font;
    this.ctx.textAlign = 'center';
    this.ctx.fillText(text, Math.floor(x), Math.floor(y));
    this.ctx.textAlign = 'left'; // Reset to default
  }

  public drawPixel(x: number, y: number, color: string = '#FFFFFF'): void {
    this.fillRect(x, y, 1, 1, color);
  }

  public drawLine(x1: number, y1: number, x2: number, y2: number, color: string = '#FFFFFF', lineWidth: number = 1): void {
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = lineWidth;
    this.ctx.beginPath();
    this.ctx.moveTo(Math.floor(x1) + 0.5, Math.floor(y1) + 0.5);
    this.ctx.lineTo(Math.floor(x2) + 0.5, Math.floor(y2) + 0.5);
    this.ctx.stroke();
  }

  public drawCircle(x: number, y: number, radius: number, filled: boolean = true, color: string = '#FFFFFF', lineWidth: number = 1): void {
    this.ctx.beginPath();
    this.ctx.arc(Math.floor(x), Math.floor(y), radius, 0, Math.PI * 2);
    
    if (filled) {
      this.ctx.fillStyle = color;
      this.ctx.fill();
    } else {
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = lineWidth;
      this.ctx.stroke();
    }
  }

  public drawPolygon(points: Vector2[], color: string = '#FFFFFF', filled: boolean = false, lineWidth: number = 1): void {
    if (points.length < 2) return;

    this.ctx.beginPath();
    this.ctx.moveTo(Math.floor(points[0].x), Math.floor(points[0].y));
    
    for (let i = 1; i < points.length; i++) {
      this.ctx.lineTo(Math.floor(points[i].x), Math.floor(points[i].y));
    }
    
    this.ctx.closePath();

    if (filled) {
      this.ctx.fillStyle = color;
      this.ctx.fill();
    } else {
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = lineWidth;
      this.ctx.stroke();
    }
  }

  public drawEllipse(x: number, y: number, radiusX: number, radiusY: number, color: string = '#FFFFFF', filled: boolean = false, lineWidth: number = 1): void {
    this.ctx.beginPath();
    this.ctx.ellipse(Math.floor(x), Math.floor(y), radiusX, radiusY, 0, 0, Math.PI * 2);
    
    if (filled) {
      this.ctx.fillStyle = color;
      this.ctx.fill();
    } else {
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = lineWidth;
      this.ctx.stroke();
    }
  }

  public drawSprite(x: number, y: number, width: number, height: number, color: string, pattern: number[][]): void {
    const pixelSize = 2; // Scale up for better visibility
    
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          this.fillRect(
            x + col * pixelSize,
            y + row * pixelSize,
            pixelSize,
            pixelSize,
            color
          );
        }
      }
    }
  }

  // C64-style character drawing
  public drawC64Char(char: string, x: number, y: number, color: string = '#FFFFFF', scale: number = 2): void {
    const charSize = 8 * scale;
    
    // Simple character patterns (8x8 pixel patterns for common characters)
    const charPatterns: { [key: string]: number[][] } = {
      '▲': [
        [0,0,0,1,1,0,0,0],
        [0,0,1,1,1,1,0,0],
        [0,1,1,1,1,1,1,0],
        [1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1],
        [0,0,0,0,0,0,0,0]
      ],
      '●': [
        [0,0,1,1,1,1,0,0],
        [0,1,1,1,1,1,1,0],
        [1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1],
        [0,1,1,1,1,1,1,0],
        [0,0,1,1,1,1,0,0]
      ],
      '◊': [
        [0,0,0,1,1,0,0,0],
        [0,0,1,0,0,1,0,0],
        [0,1,0,0,0,0,1,0],
        [1,0,0,0,0,0,0,1],
        [1,0,0,0,0,0,0,1],
        [0,1,0,0,0,0,1,0],
        [0,0,1,0,0,1,0,0],
        [0,0,0,1,1,0,0,0]
      ],
      '▓': [
        [1,0,1,0,1,0,1,0],
        [0,1,0,1,0,1,0,1],
        [1,0,1,0,1,0,1,0],
        [0,1,0,1,0,1,0,1],
        [1,0,1,0,1,0,1,0],
        [0,1,0,1,0,1,0,1],
        [1,0,1,0,1,0,1,0],
        [0,1,0,1,0,1,0,1]
      ]
    };
    
    const pattern = charPatterns[char];
    if (pattern) {
      this.drawSprite(x, y, charSize, charSize, color, pattern);
    } else {
      // Fallback to text rendering
      this.drawText(char, x, y, color, `${charSize}px "Courier New", monospace`);
    }
  }

  // Utility method to draw outlined text (for better visibility)
  public drawOutlinedText(text: string, x: number, y: number, fillColor: string = '#FFFFFF', outlineColor: string = '#000000', font: string = '14px "Courier New", monospace'): void {
    this.ctx.font = font;
    
    // Draw outline
    this.ctx.strokeStyle = outlineColor;
    this.ctx.lineWidth = 2;
    this.ctx.strokeText(text, Math.floor(x), Math.floor(y));
    
    // Draw fill
    this.ctx.fillStyle = fillColor;
    this.ctx.fillText(text, Math.floor(x), Math.floor(y));
  }

  // Draw a C64-style border
  public drawC64Border(x: number, y: number, width: number, height: number, color: string = '#FFFFFF', thickness: number = 2): void {
    // Top border
    this.fillRect(x, y, width, thickness, color);
    // Bottom border
    this.fillRect(x, y + height - thickness, width, thickness, color);
    // Left border
    this.fillRect(x, y, thickness, height, color);
    // Right border
    this.fillRect(x + width - thickness, y, thickness, height, color);
  }

  // Get text metrics
  public measureText(text: string, font: string = '14px "Courier New", monospace'): TextMetrics {
    this.ctx.font = font;
    return this.ctx.measureText(text);
  }

  // Save and restore context state
  public save(): void {
    this.ctx.save();
  }

  public restore(): void {
    this.ctx.restore();
  }

  // Alpha (opacity) control
  public setGlobalAlpha(alpha: number): void {
    this.ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  }

  public getGlobalAlpha(): number {
    return this.ctx.globalAlpha;
  }

  // Clipping
  public clipRect(x: number, y: number, width: number, height: number): void {
    this.ctx.beginPath();
    this.ctx.rect(x, y, width, height);
    this.ctx.clip();
  }

  // Get canvas context for advanced operations
  public getContext(): CanvasRenderingContext2D {
    return this.ctx;
  }
}